import { useState } from 'react'
import { Icon } from '@iconify/react'
import { Button } from '@/components/ui/button.jsx'
import { Popover, PopoverContent, PopoverContnetWithoutPortal, PopoverTrigger } from '@/components/ui/popover.jsx'
import { cn } from '@/lib/utils.js'
import { AnalyticsService } from "@/services/analytics.js";

const POPULAR_ICONS = [
    'ph:chart-line-up',
    'ph:trophy',
    'ph:video-camera',
    'ph:chart-bar',
    'ph:target',
    'ph:fire',
    'ph:lightning',
    'ph:star',
    'ph:rocket',
    'ph:medal',
    'ph:crown',
    'ph:sparkle',
    'ph:trend-down',
    'ph:circles-three-plus',
    'ph:palette',
    'ph:images',
    'ph:text-aa',
    'ph:link',
    'ph:layout',
    'ph:stack',
]

const ICON_COLORS = [
    { name: 'Gray', value: 'text-gray-500', bg: 'bg-gray-500' },
    { name: 'Primary', value: 'text-primary', bg: 'bg-primary' },
    { name: 'Blue', value: 'text-secondary-500', bg: 'bg-secondary-500' },
    { name: 'Green', value: 'text-emerald-500', bg: 'bg-emerald-500' },
    { name: 'Yellow', value: 'text-amber-500', bg: 'bg-amber-500' },
    { name: 'Red', value: 'text-red-500', bg: 'bg-red-500' },
    { name: 'Purple', value: 'text-indigo-500', bg: 'bg-indigo-500' },
    { name: 'Orange', value: 'text-orange-500', bg: 'bg-orange-500' },
    { name: 'Stone', value: 'text-stone-500', bg: 'bg-stone-500' },
]

/**
 * IconPicker Component - Notion-style icon selector
 * @param {Object} props
 * @param {Object} props.value - Current icon value { type: 'icon', value: 'ph:trophy', color: 'text-primary' }
 * @param {Function} props.onChange - Callback when icon changes
 * @param {string} props.size - Size of the icon button ('sm' | 'md' | 'lg')
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {string} props.variant - 'tile' (white glyph on a colored square, default) | 'plain' (colored glyph, no background)
 */
export const IconPicker = ({ value, onChange, size = 'md', disabled = false, noPortal = false, variant = 'tile' }) => {

    const [open, setOpen] = useState(false)
    const [selectedColor, setSelectedColor] = useState(value?.color || 'text-gray-500')

    const PopoverContentToRender = noPortal ? PopoverContnetWithoutPortal : PopoverContent

    const sizeClasses = {
        sm: 'w-6 h-6 text-base',
        md: 'w-8 h-8 text-lg',
        lg: 'w-10 h-10 text-xl'
    }

    const handleIconSelect = (iconName) => {
        onChange({ type: 'icon', value: iconName, color: selectedColor })
        AnalyticsService.track('icon-picker-icon-selected', { iconName, color: selectedColor })
    }

    const handleColorSelect = (color) => {
        setSelectedColor(color)
        AnalyticsService.track('icon-picker-color-selected', { color })
        if (value?.value) {
            onChange({ type: 'icon', value: value.value, color })
        }
    }

    const isPlain = variant === 'plain'

    const renderIcon = () => {
        // Plain: just the colored glyph, sized like a nav icon.
        if (isPlain) {
            return <Icon icon={value?.value || 'ph:chart-bar'} className={cn('h-4 w-4', value?.color || 'text-muted-foreground')} />
        }

        if (!value || !value.value) {
            return (
                <div className="w-6 h-6 rounded-md bg-gray-500 flex items-center justify-center">
                    <Icon icon="ph:chart-bar" className="w-3.5 h-3.5 text-white" />
                </div>
            )
        }

        if (value.type === 'emoji') {
            return <span className="flex items-center justify-center">{value.value}</span>
        }

        if (value.type === 'icon') {
            const bgColor = value.color ? value.color.replace('text-', 'bg-') : 'bg-primary'
            // const bgColor = 'bg-gray-200/80'
            // const textColor = value.color
            const textColor = 'text-white'
            return (
                <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', bgColor)}>
                    <Icon icon={value.value} className={cn('w-3.5 h-3.5', textColor)} />
                </div>
            )
        }

        return (
            <div className="w-6 h-6 rounded-md bg-gray-500 flex items-center justify-center">
                <Icon icon="ph:chart-bar" className="w-3.5 h-3.5 text-white" />
            </div>
        )
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    className={cn(
                        isPlain ? 'h-6 w-6' : sizeClasses[size],
                        'hover:bg-accent rounded-md transition-colors flex items-center justify-center p-0',
                        disabled && 'cursor-not-allowed opacity-50'
                    )}
                >
                    {renderIcon()}
                </Button>
            </PopoverTrigger>
            <PopoverContentToRender className="w-84 p-0" align="start" side="right" sideOffset={8}>
                <div className="p-3">
                    {/* Color Picker */}
                    <div className="mb-3">
                        <p className="text-xs font-medium mb-2 text-muted-foreground">Color</p>
                        <div className="flex gap-1.5 flex-wrap">
                            {ICON_COLORS.map((color) => (
                                <button
                                    key={color.value}
                                    onClick={() => handleColorSelect(color.value)}
                                    className={cn(
                                        'w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all',
                                        selectedColor === color.value ? 'border-gray-700 scale-110' : 'border-transparent hover:scale-105'
                                    )}
                                    title={color.name}
                                >
                                    <div className={cn('w-5 h-5 rounded-sm', color.bg)} />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Icon Grid */}
                    <div>
                        <p className="text-xs font-medium mb-2 text-muted-foreground">Icon</p>
                        <div className="grid grid-cols-5 gap-2">
                            {POPULAR_ICONS.map((iconName) => {
                                const bgColor = selectedColor.replace('text-', 'bg-')
                                const isSelected = value?.type === 'icon' && value?.value === iconName
                                if (isPlain) {
                                    return (
                                        <Button
                                            key={iconName}
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleIconSelect(iconName)}
                                            className={cn('w-full h-10 p-0 hover:bg-accent', isSelected && 'bg-accent')}
                                        >
                                            <Icon icon={iconName} className={cn('h-5 w-5', selectedColor)} />
                                        </Button>
                                    )
                                }
                                return (
                                    <Button
                                        key={iconName}
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleIconSelect(iconName)}
                                        className={cn(
                                            'w-full h-10 p-0 hover:bg-accent',
                                            value?.type === 'icon' && value?.value === iconName && 'ring-2 ring-gray-700'
                                        )}
                                    >
                                        <div className={cn('w-7 h-7 rounded-md flex items-center justify-center', bgColor)}>
                                            <Icon icon={iconName} className="w-4 h-4 text-white" />
                                        </div>
                                    </Button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </PopoverContentToRender>
        </Popover>
    )
}
